import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { cn } from '../../lib/utils';
import type { Tone } from '../../theme/colors';

export interface ActivityItem {
  id: string;
  /** Short headline of the event. */
  title: React.ReactNode;
  /** Optional secondary description (one line). */
  description?: React.ReactNode;
  /** When the event happened — rendered verbatim, format it yourself. */
  timestamp?: React.ReactNode;
  /** Lucide icon — colored according to `tone`. */
  icon?: LucideIcon;
  /** Tone applied to the leading dot / icon chip. */
  tone?: Tone;
  /** Optional click handler — turns the row into a button. */
  onClick?: () => void;
}

export interface ActivityPanelProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Panel title. */
  title?: React.ReactNode;
  /** Right-aligned header actions ("View all", filter). */
  headerActions?: React.ReactNode;
  /** Either supply a `items` array OR render rows manually as `children`. */
  items?: ActivityItem[];
  /** Limit rendered rows. Default: all. */
  limit?: number;
  /** Footer row (e.g. "Show more"). */
  footer?: React.ReactNode;
  /** Custom empty state when items is empty. */
  emptyState?: React.ReactNode;
  /** Loading skeleton count. */
  loadingCount?: number;
  loading?: boolean;
}

const toneToChip: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  primary: 'bg-primary/10 text-primary',
  accent: 'bg-accent/10 text-accent',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
  destructive: 'bg-destructive/10 text-destructive',
  info: 'bg-info/10 text-info',
};

/**
 * ActivityPanel — chronological list of events, audit entries, or feed items.
 *
 * Accepts a structured `items` array (the common case) or arbitrary
 * `children`. Tone-aware: every row renders its icon inside a tone-colored
 * chip while the rest of the row stays neutral.
 */
export const ActivityPanel = React.forwardRef<
  HTMLDivElement,
  ActivityPanelProps
>(
  (
    {
      title = 'Recent activity',
      headerActions,
      items,
      limit,
      footer,
      emptyState,
      loadingCount = 4,
      loading,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const rendered = items ? (limit ? items.slice(0, limit) : items) : null;

    return (
      <Card
        ref={ref}
        className={cn('flex flex-col', className)}
        {...props}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <h3 className="text-sm font-semibold tracking-tight text-foreground">
            {title}
          </h3>
          {headerActions && (
            <div className="flex items-center gap-1.5">{headerActions}</div>
          )}
        </CardHeader>
        <CardContent className="flex-1 p-0">
          {loading ? (
            <ul className="divide-y divide-border">
              {Array.from({ length: loadingCount }).map((_, idx) => (
                <li key={idx} className="flex items-center gap-3 px-5 py-3">
                  <span className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-muted/70" />
                  <div className="flex flex-1 flex-col gap-1.5">
                    <span className="h-3 w-2/3 animate-pulse rounded bg-muted/70" />
                    <span className="h-2.5 w-1/3 animate-pulse rounded bg-muted/70" />
                  </div>
                </li>
              ))}
            </ul>
          ) : rendered && rendered.length > 0 ? (
            <ul className="divide-y divide-border">
              {rendered.map((item) => {
                const Icon = item.icon;
                const tone = item.tone ?? 'neutral';
                const Row = item.onClick ? 'button' : 'div';
                return (
                  <li key={item.id}>
                    <Row
                      type={item.onClick ? 'button' : undefined}
                      onClick={item.onClick}
                      className={cn(
                        'flex w-full items-start gap-3 px-5 py-3 text-left transition-colors',
                        item.onClick && 'hover:bg-muted/50 focus:bg-muted/50 focus:outline-none',
                      )}
                    >
                      <span
                        className={cn(
                          'mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                          toneToChip[tone],
                        )}
                      >
                        {Icon ? (
                          <Icon className="h-3.5 w-3.5" />
                        ) : (
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {item.title}
                        </div>
                        {item.description && (
                          <div className="mt-0.5 truncate text-xs text-muted-foreground">
                            {item.description}
                          </div>
                        )}
                      </div>
                      {item.timestamp && (
                        <span className="shrink-0 text-[11px] uppercase tracking-wider text-muted-foreground">
                          {item.timestamp}
                        </span>
                      )}
                    </Row>
                  </li>
                );
              })}
            </ul>
          ) : children ? (
            <div>{children}</div>
          ) : (
            (emptyState ?? (
              <div className="px-5 py-10 text-center">
                <p className="text-sm font-medium text-foreground">
                  No recent activity
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  As soon as anything happens, you'll see it here.
                </p>
              </div>
            ))
          )}
        </CardContent>
        {footer && (
          <div className="border-t border-border px-5 py-2.5 text-xs text-muted-foreground">
            {footer}
          </div>
        )}
      </Card>
    );
  },
);
ActivityPanel.displayName = 'ActivityPanel';
